import { LitElement, html, nothing } from 'lit';
import type { JudgmentPacketRead } from '../types';
import { canonicalJudgmentPreparationView, getCanonicalJudgmentSelectionSurface } from '../canonical-judgment';
import { currentLang } from '../i18n';
import { defineZenComponent } from './register';

export class ZenCanonicalJudgmentPanel extends LitElement {
  static properties = {
    goalId: { type: String },
    packet: { attribute: false },
    conditionRef: { attribute: false },
    axis: { attribute: false },
    pending: { type: Boolean },
    chosenOptionKey: { attribute: false },
  };

  declare goalId: string;
  declare packet: JudgmentPacketRead | null;
  /** #2422 stage 2: the judgment this packet belongs to, when the Goal has several. */
  declare conditionRef: string | null;
  declare axis: string | null;
  declare pending: boolean;
  /** The option just chosen, shown as chosen while it is saved and read back. */
  declare chosenOptionKey: string | null;

  constructor() {
    super();
    this.goalId = '';
    this.packet = null;
    this.conditionRef = null;
    this.axis = null;
    this.pending = false;
    this.chosenOptionKey = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.display = 'contents';
  }

  private selectOption(optionKey: string, optionValue: string) {
    if (this.pending) return;
    const surface = getCanonicalJudgmentSelectionSurface(this.packet);
    const option = surface?.options.find(candidate => candidate.key === optionKey && candidate.value === optionValue);
    if (!surface || !option || !this.packet?.selection_snapshot_token) return;
    this.dispatchEvent(new CustomEvent('canonical-judgment-select', {
      bubbles: true,
      composed: true,
      detail: {
        goalId: this.goalId,
        optionKey: option.key,
        optionValue: option.value,
        selectionSnapshotToken: this.packet.selection_snapshot_token,
        ...(this.conditionRef && this.axis ? { conditionRef: this.conditionRef, axis: this.axis } : {}),
      },
    }));
  }

  /** #2537: put the current choice on hold; the judgment is open again. */
  private holdChoice() {
    if (this.pending || !this.packet?.selection_snapshot_token) return;
    this.dispatchEvent(new CustomEvent('canonical-judgment-hold', {
      bubbles: true,
      composed: true,
      detail: {
        goalId: this.goalId,
        selectionSnapshotToken: this.packet.selection_snapshot_token,
        ...(this.conditionRef && this.axis ? { conditionRef: this.conditionRef, axis: this.axis } : {}),
      },
    }));
  }

  /** #2951: send the place typed in a place card's box. */
  private sendText(event: Event) {
    event.preventDefault();
    const box = this.packet?.text_input;
    const field = this.querySelector<HTMLInputElement>('.canonical-judgment-text-input');
    const text = field?.value.trim() ?? '';
    if (this.pending || !box || !text || !this.packet?.selection_snapshot_token) return;
    this.dispatchEvent(new CustomEvent('canonical-judgment-select', {
      bubbles: true,
      composed: true,
      detail: {
        goalId: this.goalId,
        optionKey: box.option_key,
        optionValue: box.option_value,
        selectionSnapshotToken: this.packet.selection_snapshot_token,
        text,
        ...(this.conditionRef && this.axis ? { conditionRef: this.conditionRef, axis: this.axis } : {}),
      },
    }));
  }

  /** #2951: a place card with no place written: the question, then a box to type the place in. */
  private renderTextInput() {
    const packet = this.packet;
    const box = packet?.text_input;
    if (!packet || !box || !packet.selection_snapshot_token) return nothing;
    const en = currentLang === 'en';
    return html`
      <section class="canonical-judgment-panel" aria-label=${en ? 'Where it is held' : '場所'}>
        <div class="canonical-judgment-heading">
          <span class="mi mi-sm">location_on</span>
          <span>${en ? 'Place' : '場所'}</span>
        </div>
        ${packet.question ? html`<p class="canonical-judgment-question">${packet.question}</p>` : nothing}
        ${this.renderTextBox(box)}
      </section>
    `;
  }

  /** The box to type a place in, or the place typed. */
  private renderTextBox(box: NonNullable<JudgmentPacketRead['text_input']>) {
    return box.value
      ? html`<p class="canonical-judgment-question"><span class="mi mi-xs" aria-hidden="true">check_circle</span>${box.value}</p>`
      : html`<div class="canonical-judgment-options work-text-option" role="group">
          <input type="text" class="canonical-judgment-text-input work-text-input" maxlength="200" placeholder=${box.placeholder} ?disabled=${this.pending}
            @keydown=${(event: KeyboardEvent) => { if (event.key === 'Enter' && !event.isComposing) this.sendText(event); }}>
          <button type="button" class="canonical-judgment-option" ?disabled=${this.pending} @click=${(event: Event) => this.sendText(event)}>
            <span class="canonical-judgment-option-label">${box.option_value}</span>
          </button>
        </div>`;
  }

  render() {
    // #2936: a card with options and a box (the base card) shows both; a box alone is the place card's.
    if (this.packet?.text_input && !this.packet.option_candidates?.length) return this.renderTextInput();
    const surface = getCanonicalJudgmentSelectionSurface(this.packet);
    if (!surface) return nothing;
    const selected = this.chosenOptionKey ?? surface.selectedOptionKey;
    // #2537: only a choice the server holds can be put on hold.
    const canHold = !this.pending && this.chosenOptionKey === null && Boolean(surface.selectedOptionKey);
    // #2859: what the assistant checked before this judgment.
    const prep = canonicalJudgmentPreparationView(surface, currentLang);
    return html`
      <section class="canonical-judgment-panel" aria-label=${currentLang === 'en' ? 'Choose an option' : '選択肢を選ぶ'}>
        <div class="canonical-judgment-heading">
          <span class="mi mi-sm">gavel</span>
          <span>${currentLang === 'en' ? 'Your decision' : 'あなたの判断'}</span>
        </div>
        ${surface.packet.question ? html`<p class="canonical-judgment-question">${surface.packet.question}</p>` : nothing}
        ${prep && prep.checks.length > 0 ? html`
          <div class="decision-materials canonical-judgment-materials">
            <div class="decision-materials-title">${currentLang === 'en' ? 'Calendar check' : '予定の確認'}</div>
            ${prep.checks.map(line => html`<div class="decision-material-item"><span class="mi mi-xs">event</span><span>${line}</span></div>`)}
          </div>
        ` : nothing}
        <div class="canonical-judgment-options" role="group">
          ${surface.options.map(option => html`
            <button
              type="button"
              class=${`canonical-judgment-option${selected === option.key ? ' selected' : ''}${option.recommended ? ' recommended' : ''}`}
              ?disabled=${this.pending}
              aria-pressed=${selected === option.key ? 'true' : 'false'}
              @click=${() => this.selectOption(option.key, option.value as string)}
            >
              <span class="canonical-judgment-option-label">${option.label}</span>
              ${prep?.optionReasons.get(option.key) ? html`<span class="canonical-judgment-option-reason">${prep.optionReasons.get(option.key)}</span>` : nothing}
              ${selected === option.key ? html`<span class="mi mi-xs" aria-label=${currentLang === 'en' ? 'Selected' : '選択済み'}>check_circle</span>` : nothing}
            </button>
          `)}
        </div>
        ${prep?.recommendation ? html`
          <p class="canonical-judgment-recommendation">
            <span class="mi mi-sm">lightbulb</span>
            <span>${currentLang === 'en'
              ? `Recommended: ${prep.recommendation.label}${prep.recommendation.reason ? ` (${prep.recommendation.reason})` : ''}`
              : `「${prep.recommendation.label}」を推奨します${prep.recommendation.reason ? `（${prep.recommendation.reason}）` : ''}`}</span>
          </p>
        ` : nothing}
        ${surface.packet.text_input ? this.renderTextBox(surface.packet.text_input) : nothing}
        ${canHold ? html`
          <button type="button" class="canonical-judgment-hold" @click=${() => this.holdChoice()}>
            <span class="mi mi-sm">pause_circle</span>
            <span>${currentLang === 'en' ? 'Put on hold' : '保留にする'}</span>
          </button>
        ` : nothing}
      </section>
    `;
  }
}

defineZenComponent('zen-canonical-judgment-panel', ZenCanonicalJudgmentPanel);
